import { FC, useEffect, useState } from 'react'

import {
  ActForm,
  Organization,
  UUID,
  AssigneesProjectResponseType,
} from '@builddocs/data-management'
import {
  AOSRType,
  SecondaryActType,
  ActFormKeys,
  Application,
  LoadableApplicationKeys,
} from '@builddocs/filestore-api'
import { CommentsViewer } from '@builddocs/mui'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import ruLocale from 'date-fns/locale/ru'
import { motion } from 'framer-motion'

import { getEditField, getEditFieldSecondaryActs } from './Actions/getEditField'
import {
  getValueFieldForSecondary,
  getValueFieldForAOSR,
} from './Actions/getValueField'
import {
  AOSRJsonType,
  ListsFieldsType,
  SecondaryActJsonType,
} from './Types/dataJSON'
import { ActRenderer } from './viewer/ActRenderer'
import { ActSectionBase, RowDataType } from './viewer/template.type'

type ownProps = {
  act: AOSRType | SecondaryActType
  assigneesForActRegistry: Record<string, UUID[]> | null
  zoom: number
  dataJSON: AOSRJsonType | SecondaryActJsonType
  onChange: (e: string, address: string) => void
  onRejectDocs: (address: string) => void
  initValue: AOSRJsonType | SecondaryActJsonType
  isLoading: boolean
  isOpenEditor: boolean
  isOpenComments: boolean
  shouldGenerateRegistry?: boolean
  showCertAOSR?: boolean
  onSelectDoc: (
    docs: Record<string, any>[],
    repository: string,
    address?: string,
  ) => void
  onDeviate?: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
  ) => void
  onSelectAssignees: (
    signers: AssigneesProjectResponseType[],
    type: string,
    showPosition?: boolean,
  ) => void
  onSelectOrg: (org: Organization | undefined, type: string) => void
  onSelectMaterial: (materialIds: UUID[], key: string) => void
  onSelectWork: (sectionIds: UUID[], key: string) => void
  onGetFields: (fields: ListsFieldsType) => void
  onReloadAct: () => void
  onAddRowClick: (key: string, rows: RowDataType[]) => void
  onRemoveRowClick?: (key: string, rowIndex: number) => void
  onShowCertAOSR: () => void
  onGenerateRegistry: () => void
  onSelectOrgToField: (
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
    key: string,
  ) => void
  onSelectOrgToFieldForAOSR: (
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
    key: string,
  ) => void
  onSelectAssigneesForAOSR: (signers: unknown[] | unknown, key: string) => void
  onSelectShowInfoAboutOrg: (key: string | string[]) => void
  forceIncludeOrganization: string[] | undefined
  onEditRepresentativesInRegistry: (reps: Record<string, UUID[]>) => void
  setShouldIncludeSupportingsInProjectDocuments: (value: boolean) => void
  shouldIncludeSupportingsInProjectDocuments: boolean
  shouldShowMaterials: boolean
  shouldShowLaboratories: boolean
  shouldShowExecutiveSchemes: boolean
  onAddMaterials: (value: boolean) => void
  onAddLaboratories: (value: boolean) => void
  onAddExecutiveSchemes: (value: boolean) => void
}

const FieldsEditor: FC<ownProps> = (props: ownProps) => {
  const {
    act,
    assigneesForActRegistry,
    zoom,
    dataJSON,
    onChange,
    onRejectDocs,
    initValue,
    isLoading,
    isOpenEditor,
    isOpenComments,
    shouldGenerateRegistry = false,
    showCertAOSR = false,
    onSelectDoc,
    onDeviate,
    onSelectAssignees,
    onGetFields,
    onReloadAct,
    onSelectOrg,
    onSelectMaterial,
    onSelectWork,
    onAddRowClick,
    onRemoveRowClick,
    onShowCertAOSR,
    onGenerateRegistry,
    onSelectOrgToFieldForAOSR,
    onSelectAssigneesForAOSR,
    onSelectOrgToField,
    onSelectShowInfoAboutOrg,
    forceIncludeOrganization,
    onEditRepresentativesInRegistry,
    shouldIncludeSupportingsInProjectDocuments,
    setShouldIncludeSupportingsInProjectDocuments,
    shouldShowMaterials,
    shouldShowLaboratories,
    shouldShowExecutiveSchemes,
    onAddMaterials,
    onAddLaboratories,
    onAddExecutiveSchemes,
  } = props

  const [state, setState] = useState<{
    key: string
    title: string
    value: string
    editable: ActSectionBase['editable']
    dataType: ActSectionBase['dataType']
    options?: string[]
    documentTypes?: LoadableApplicationKeys[]
    showPosition?: boolean
  }>({
    key: '',
    title: '',
    value: '',
    editable: undefined,
    dataType: undefined,
    options: undefined,
    documentTypes: undefined,
  })

  const [commentEntity, setCommentEntity] = useState<{
    place: string
    docType:
      | ActFormKeys
      | Application.CERTIFICATE
      | Application.EXECUTIVE_SCHEME
      | Application.LABORATORY
    docId: string
    name: string
  }>({
    place: state.title,
    docType: dataJSON.act_type,
    docId: act.id,
    name: act.closingName ?? act.name,
  })

  useEffect(() => {
    setCommentEntity({
      place: state.title,
      docType: dataJSON.act_type,
      docId: act.id,
      name: act.closingName ?? act.name,
    })
  }, [state.key, isOpenComments])

  if (isLoading) {
    return <CircularProgress />
  }

  if (!dataJSON.act_type) {
    // проверка наличия типа акта в JSON (временная мера, для старых версий JSON). Если типа нет, то акт необходимо перегенерить.
    return (
      <Stack spacing={2}>
        <Typography>Необходимо обновить акт</Typography>
        <Button
          size="small"
          onClick={() => {
            onReloadAct()
          }}
        >
          Обновить
        </Button>
      </Stack>
    )
  }

  if (act.regenerationRequired && isOpenEditor) {
    return (
      <Stack
        alignItems="center"
        direction="column"
        gap="8px"
        height="100%"
        justifyContent="center"
      >
        <Typography>
          Редактирование акта заблокировано. Чтобы внести изменения, пожалуйста,
          перегенерируйте акт.
        </Typography>
        <Button
          onClick={() => {
            onReloadAct()
          }}
        >
          Перегенерировать
        </Button>
      </Stack>
    )
  }

  return (
    <Stack direction="row" height="100%" spacing={2}>
      <motion.div
        animate={{ zoom }}
        transition={{ ease: 'easeOut', duration: 0.2 }}
      >
        <Box
          height="100%"
          left={0}
          position="sticky"
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            direction: 'rtl',
          }}
          top={0}
        >
          <Box
            style={{
              direction: 'ltr',
            }}
          >
            <ActRenderer
              actItems={dataJSON}
              getValue={({ value, sectionTemplate, template }) =>
                dataJSON.act_type === ActForm.AOSR
                  ? getValueFieldForAOSR({
                      value,
                      dataJSON,
                      sectionTemplate,
                      showCertAOSR,
                      template,
                      forceIncludeOrganization,
                      shouldIncludeSupportingsInProjectDocuments,
                    })
                  : getValueFieldForSecondary({
                      value,
                      dataJSON,
                      sectionTemplate,
                      template,
                      shouldShowMaterials,
                      shouldShowLaboratories,
                      shouldShowExecutiveSchemes,
                    })
              }
              selectedField={state?.key}
              onAddRowClick={onAddRowClick}
              onChange={onChange}
              onGetFields={onGetFields}
              onRemoveRowClick={onRemoveRowClick}
              onSelectShowInfoAboutOrg={onSelectShowInfoAboutOrg}
              onToggle={v =>
                setState({
                  value: v.value as string,
                  key: v.key,
                  title: v.title,
                  editable: v.editable,
                  dataType: v.dataType,
                  options: v.options,
                  documentTypes: v.documentTypes,
                  showPosition: v.showPosition,
                })
              }
            />
          </Box>
        </Box>
      </motion.div>
      {isOpenComments && (
        <CommentsViewer
          showHistory
          closingId={act.closingId}
          commentEntity={commentEntity}
          onDeviate={onDeviate}
        />
      )}
      {isOpenEditor && (
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={ruLocale}>
          <Box
            height="100%"
            style={{ overflowY: 'auto', overflowX: 'hidden' }}
            sx={{ pb: 1 }}
            width="100%"
          >
            {state.editable ? (
              dataJSON.act_type === ActForm.AOSR ? (
                getEditField({
                  state,
                  dataJSON,
                  act: act as AOSRType,
                  onChange,
                  onSelectDoc,
                  onRejectDocs,
                  onShowCertAOSR,
                  showCertAOSR,
                  onGenerateRegistry,
                  shouldGenerateRegistry,
                  onSelectAssigneesForAOSR,
                  onSelectOrgToFieldForAOSR,
                  onSelectShowInfoAboutOrg,
                  forceIncludeOrganization,
                  onEditRepresentativesInRegistry,
                  assigneesForActRegistry,
                  shouldIncludeSupportingsInProjectDocuments,
                  setShouldIncludeSupportingsInProjectDocuments,
                })
              ) : (
                getEditFieldSecondaryActs({
                  state,
                  dataJSON,
                  initValue: initValue as SecondaryActJsonType,
                  onChange,
                  onSelectDoc,
                  onRejectDocs,
                  onSelectAssignees,
                  onSelectOrg,
                  onSelectMaterial,
                  onSelectWork,
                  onSelectOrgToField,
                  onGenerateRegistry,
                  shouldGenerateRegistry,
                  onEditRepresentativesInRegistry,
                  assigneesForActRegistry,
                  shouldShowMaterials,
                  shouldShowLaboratories,
                  shouldShowExecutiveSchemes,
                  onAddMaterials,
                  onAddLaboratories,
                  onAddExecutiveSchemes,
                  onSelectShowInfoAboutOrg,
                  forceIncludeOrganization,
                })
              )
            ) : (
              <Typography variant="h6">
                Выберите поле, чтобы отредактировать. Чтобы оставлять замечания,
                перейдите в режим «Замечания».
              </Typography>
            )}
          </Box>
        </LocalizationProvider>
      )}
    </Stack>
  )
}

export default FieldsEditor
